import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Order } from '@harbor/shared';
import { nextOrderStatuses, type OrderStatus } from '@harbor/shared';
import { ApiError, apiRequest } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { money, dateTime, titleCase, orderStatusTone } from '../lib/format.js';
import { Alert, Badge, Button, Card, EmptyState, Skeleton } from '../components/ui.js';

/**
 * Order detail.
 *
 * The cancel button is rendered from `nextOrderStatuses(order.status)` — the
 * same adjacency map the API validates against. That is why the button can never
 * offer an action the server will refuse: the rule exists once, in the shared
 * package, and both sides read it.
 */
export function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, refetch } = useApi<{ order: Order }>(id ? `/api/orders/${id}` : null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  if (loading && !data) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-12 sm:px-6">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 sm:px-6">
        <EmptyState
          title="Order not found"
          description={error?.message ?? 'That order does not exist, or it does not belong to your account.'}
          action={
            <Link
              to="/orders"
              className="inline-flex h-11 items-center rounded-lg border border-line bg-surface px-4 text-sm font-medium"
            >
              Back to orders
            </Link>
          }
        />
      </div>
    );
  }

  const order = data.order;
  const canCancel = (nextOrderStatuses(order.status) as OrderStatus[]).includes('cancelled');

  async function cancel() {
    setCancelling(true);
    setCancelError(null);
    try {
      await apiRequest(`/api/orders/${order.id}/cancel`, { method: 'POST' });
      refetch();
    } catch (caught) {
      setCancelError(
        caught instanceof ApiError ? caught.problem.detail : 'Could not cancel this order',
      );
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <Link to="/orders" className="text-sm text-muted transition-colors hover:text-ink">
        ← All orders
      </Link>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="tabular text-2xl font-semibold tracking-tight">{order.number}</h1>
          <p className="mt-1 text-sm text-muted">Placed {dateTime(order.placedAt)}</p>
        </div>
        <Badge tone={orderStatusTone(order.status)}>{titleCase(order.status)}</Badge>
      </div>

      {cancelError && (
        <div className="mt-6">
          <Alert tone="danger" title="Could not cancel" onDismiss={() => setCancelError(null)}>
            {cancelError}
          </Alert>
        </div>
      )}

      <Card className="mt-6 overflow-hidden">
        <ul className="divide-y divide-line">
          {order.items.map((item) => (
            <li key={item.id} className="flex items-center gap-4 px-5 py-4">
              <div className="size-16 shrink-0 overflow-hidden rounded-lg border border-line bg-sunken">
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt="" className="size-full object-cover" />
                ) : null}
              </div>
              <div className="min-w-0 flex-1">
                {item.slug ? (
                  <Link to={`/product/${item.slug}`} className="truncate text-sm font-medium hover:text-accent">
                    {item.name}
                  </Link>
                ) : (
                  <p className="truncate text-sm font-medium">{item.name}</p>
                )}
                <p className="tabular text-xs text-subtle">
                  {item.quantity} × {money(item.unitPriceMinor, order.currency)}
                </p>
              </div>
              <span className="tabular text-sm">{money(item.lineTotalMinor, order.currency)}</span>
            </li>
          ))}
        </ul>

        <dl className="space-y-1.5 border-t border-line bg-raised px-5 py-4 text-sm">
          <Row label="Subtotal" value={money(order.subtotalMinor, order.currency)} />
          <Row
            label="Shipping"
            value={order.shippingMinor === 0 ? 'Free' : money(order.shippingMinor, order.currency)}
          />
          <Row label="Tax" value={money(order.taxMinor, order.currency)} />
          <div className="flex items-center justify-between border-t border-line pt-2 text-base font-semibold">
            <dt>Total</dt>
            <dd className="tabular">{money(order.totalMinor, order.currency)}</dd>
          </div>
        </dl>
      </Card>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Card className="p-5">
          <h2 className="text-xs font-medium uppercase tracking-wide text-subtle">Shipping address</h2>
          <address className="mt-2 text-sm not-italic leading-relaxed text-muted">
            {order.shippingAddress.name}
            <br />
            {order.shippingAddress.line1}
            {order.shippingAddress.line2 && (
              <>
                <br />
                {order.shippingAddress.line2}
              </>
            )}
            <br />
            {order.shippingAddress.city}
            {order.shippingAddress.region ? `, ${order.shippingAddress.region}` : ''}{' '}
            {order.shippingAddress.postalCode}
            <br />
            {order.shippingAddress.country}
          </address>
        </Card>

        <Card className="p-5">
          <h2 className="text-xs font-medium uppercase tracking-wide text-subtle">Payment</h2>
          <p className="tabular mt-2 break-all text-sm text-muted">
            {order.paymentReference ?? 'No payment reference recorded'}
          </p>
          <p className="mt-3 text-xs text-subtle">
            Last updated {dateTime(order.updatedAt)}
          </p>
        </Card>
      </div>

      {canCancel && (
        <div className="mt-6 flex justify-end">
          <Button variant="danger" loading={cancelling} onClick={() => void cancel()}>
            Cancel this order
          </Button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted">{label}</dt>
      <dd className="tabular">{value}</dd>
    </div>
  );
}
